import { Card, Flex } from "antd";
import { SmsReportQuery, SmsStatistics, useGetSmsReport } from "../../hooks/apis/reports.api";
import { useEffect, useState } from "react";
import type { Dispatch, SetStateAction } from "react";
import MessagesTable from "../../components/Reports/Table/Messages";
import { NavigateFunction, useNavigate, useSearchParams } from "react-router-dom";
import { pageChange } from "../../utils/PageChange";
import MessageStatistics from "../../components/Reports/Statistics/MessagesStatistics";
import { ReportFilters } from "../../components/Reports/Filters";
import { FiltersPanel } from "../../components/Reports/Filters/Header";
import Loader from "../../components/generic/Loader";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import { RootState } from "../../store/store";
import { BulkMessageStatistics, useGetBulkMessagesJobs } from "../../hooks/apis/bulkmessages.api";
import BulkMessagesTable from "../../components/Reports/Table/BulkMessages";
import BulkMessageStatisticsContainer from "../../components/Reports/Statistics/BulkMessageStatistics";
import { useGetUsersByDids } from "../../hooks/apis/user.api";

const reportsContainerStyle: React.CSSProperties = {
    width: "100%",
    height: "100%"
};

const ReportsPage = () => {
    const navigate: NavigateFunction = useNavigate();
    const [searchParams] = useSearchParams();

    const page = parseInt(searchParams.get("page") || "1");
    const start_date = searchParams.get("start_date");
    const end_date = searchParams.get("end_date");
    const type: string | null = searchParams.get("type");


    const inititalFilters: SmsReportQuery = {
        offset: (page - 1) * 10,
        limit: 20,
        start_date: start_date ? new Date(start_date) : new Date(dayjs().startOf("day").toISOString()),
        end_date: end_date ? new Date(end_date) : new Date(dayjs().endOf("day").toISOString()),
        start_time: null,
        end_time: null,
        direction: null,
        user: null,
        dids: null,
        status: null,
        searchInput: null
    };

    const { selectedAccount } = useSelector((state: RootState) => state.admin);
    const { data: smsDids } = useSelector((state: RootState) => state.smsDids);

    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [statistics, setStatistics] = useState<SmsStatistics | null>(null);
    const [bulkMessagesStatistics, setBulkMessagesStatistics] = useState<BulkMessageStatistics | null>(null);
    const [filters, setFilters] = useState(inititalFilters);
    const [pendingFilters, setPendingFilters] = useState(inititalFilters);
    const [messagesType, setMessagesTypeState] = useState<"messages" | "bulk_messages">((type as "messages" | "bulk_messages") || "messages");

    useEffect(() => {
        setFilters(prev => ({
            ...prev,
            offset: inititalFilters.offset,
            start_date: inititalFilters.start_date,
            end_date: inititalFilters.end_date
        }));
        setPendingFilters(prev => ({
            ...prev,
            offset: inititalFilters.offset,
            start_date: inititalFilters.start_date,
            end_date: inititalFilters.end_date
        }));
    }, [page, start_date, end_date]);

    const { data: reports, refetch: refetchReports, isRefetching: isRefetchingReports, isLoading: isLoadingReports } = useGetSmsReport(filters);
    const { data: bulkMessages, refetch: refetchBulkMessages, isRefetching: isRefetchingBulkMessages, isLoading: isLoadingBulkMessages } = useGetBulkMessagesJobs(filters);

    const { refetch: refetchUsers } = useGetUsersByDids(smsDids.map(did => did.did));

    if (!filters) {
        return <Loader />;
    }

    useEffect(() => {
        if (selectedAccount) {
            refetchUsers();
        }
    }, [selectedAccount]);

    useEffect(() => {
        if (messagesType === "messages") {
            refetchReports();
        } else if (messagesType === "bulk_messages") {
            refetchBulkMessages();
        }
    }, [filters, messagesType, selectedAccount]);

    useEffect(() => {
        if (reports && reports.statistics) {
            if (Math.ceil(reports.statistics.total / filters.limit) < page && page > 1) {
                const newPage = Math.ceil(reports.statistics.total / filters.limit);
                pageChange(newPage, navigate);
            }
            setStatistics(reports.statistics);
        }
    }, [reports]);

    useEffect(() => {
        if (bulkMessages && bulkMessages.statistics) {
            if (Math.ceil(bulkMessages.statistics.total / filters.limit) < page && page > 1) {
                const newPage = Math.ceil(bulkMessages.statistics.total / filters.limit);
                pageChange(newPage, navigate);
            }
            setBulkMessagesStatistics(bulkMessages.statistics);
        }
    }, [bulkMessages]);

    const buildReportsUrl = (targetFilters: SmsReportQuery, overrideType?: "messages" | "bulk_messages") => {
        const params = new URLSearchParams();
        params.set("page", "1");

        if (targetFilters.start_date) {
            params.set("start_date", targetFilters.start_date.toISOString());
        }

        if (targetFilters.end_date) {
            params.set("end_date", targetFilters.end_date.toISOString());
        }

        if (overrideType ?? messagesType) {
            params.set("type", (overrideType ?? messagesType) as string);
        }

        return `/reports?${params.toString()}`;
    };

    const handleApplyFilters = (nextFilters?: SmsReportQuery) => {
        const updatedFilters = {
            ...(nextFilters || pendingFilters),
            offset: 0
        };

        setFilters(updatedFilters);
        setPendingFilters(updatedFilters);
        navigate(buildReportsUrl(updatedFilters));
    };

    const handleMessagesTypeChange: Dispatch<SetStateAction<"messages" | "bulk_messages">> = value => {
        setMessagesTypeState(prev => {
            const nextValue = typeof value === "function" ? value(prev) : value;
            navigate(buildReportsUrl(filters, nextValue));
            return nextValue;
        });
    };

    const handleDateChange = ({ start_date, end_date }: { start_date: Date; end_date: Date }) => {
        const updatedFilters: SmsReportQuery = {
            ...filters,
            start_date,
            end_date,
            offset: 0
        };

        setFilters(updatedFilters);
        setPendingFilters(prev => ({
            ...prev,
            start_date,
            end_date
        }));
        navigate(buildReportsUrl(updatedFilters));
    };

    const handleSearchChange = (value: string) => {
        const normalized = value?.trim();

        setPendingFilters(prev => ({
            ...prev,
            searchInput: normalized || null
        }));

        setFilters(prev => ({
            ...prev,
            searchInput: normalized || null,
            offset: 0
        }));
    };

    const clearFilters = () => {
        handleApplyFilters(inititalFilters);
    };

    return (
        <div style={{ height: "100vh", overflow: "auto" }}>
            <Flex vertical gap={10} style={{ padding: "20px" }}>
                <Flex gap={10} vertical style={reportsContainerStyle}>
                    {messagesType === "messages" ? (
                        <MessageStatistics statistics={statistics} />
                    ) : (
                        <BulkMessageStatisticsContainer statistics={bulkMessagesStatistics} />
                    )}
                </Flex>
                <Card bordered={false} className="criclebox cardbody h-full">
                    <FiltersPanel
                        clearFilters={clearFilters}
                        filters={pendingFilters}
                        setFilters={setPendingFilters}
                        isFilterOpen={isFilterOpen}
                        setIsFilterOpen={setIsFilterOpen}
                        messagesType={messagesType}
                        setMessagesType={handleMessagesTypeChange}
                        onSearchChange={handleSearchChange}
                        onDateChange={handleDateChange}
                    />
                    {isFilterOpen && (
                        <ReportFilters
                            type={messagesType}
                            loading={isLoadingReports}
                            filters={pendingFilters}
                            setFilters={setPendingFilters}
                            applyFilters={() => handleApplyFilters()}
                        />
                    )}
                    {messagesType === "messages" ? (
                        <MessagesTable
                            filters={filters}
                            setFilters={setFilters}
                            isLoading={isLoadingReports || isRefetchingReports || isLoadingBulkMessages || isRefetchingBulkMessages}
                            statistics={statistics}
                            messages={reports?.messages || []}
                            page={page ? page : 1}
                        />
                    ) : (
                        <BulkMessagesTable
                            filters={filters}
                            setFilters={setFilters}
                            isLoading={isLoadingBulkMessages || isRefetchingBulkMessages || isLoadingReports || isRefetchingReports}
                            statistics={bulkMessagesStatistics}
                            messages={bulkMessages?.messages || []}
                            page={page ? page : 1}
                        />
                    )}
                </Card>
            </Flex>
        </div>
    );
};

export default ReportsPage;
